import { Fragment, useRef } from "react";
import { Link } from "react-router";
import { getFormattedCaaSImageUrl } from "@components/OptimisedImage/utils/helpers";
import { useOnLoadImages } from "@hooks/useOnLoadImages";
import { DEFAULT_CAAS_FILTER } from "@routes/Article/components/ArticleThumbnail/utils/contants";
import { ArticleThumbnailProps } from "@routes/Article/components/ArticleThumbnail/utils/types";
import { cn } from "@util/helpers";

/**
 * ArticleThumbnail component that renders an article thumbnail image with optional filters and styles.
 * It supports single and multiple image filters, as well as a blurred background effect.
 */
const ArticleThumbnail = ({
  className,
  articleLink,
  media,
  filters,
  orientation = "original",
  withBlurBackground = false,
  imageClassName,
}: ArticleThumbnailProps): React.ReactElement => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imagesLoaded = useOnLoadImages(wrapperRef);
  const image = media[orientation] || media["landscape"] || media["original"];

  if (!image || !image.url) {
    return <></>;
  }

  const _filters = filters || DEFAULT_CAAS_FILTER;
  const imgSrc = getFormattedCaaSImageUrl(image.url, _filters);

  const LinkComponent: React.ElementType = articleLink ? Link : Fragment;

  const props = articleLink
    ? {
        to: articleLink,
        reloadDocument: true,
        className: cn("block h-full w-full"),
      }
    : {};

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden",
        {
          "article-main-image--loaded": imagesLoaded,
        },
        className
      )}
      ref={wrapperRef}
      data-testid="article-thumbnail-component"
    >
      {withBlurBackground ? (
        <div
          style={{
            background: `center / cover no-repeat url(${imgSrc})`,
          }}
          className="absolute top-0 z-[-1] h-full w-full overflow-hidden blur-[100px]"
        />
      ) : null}

      <LinkComponent {...props}>
        <img
          className={cn(
            "relative z-10 h-full w-full object-contain",
            imageClassName
          )}
          src={imgSrc}
          alt={media.alt || media.caption || ""}
          width={image?.width || 0}
          height={image?.height || 0}
        />
      </LinkComponent>
    </div>
  );
};

export default ArticleThumbnail;
